import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as LuIcons from 'react-icons/lu';
const { LuSearch, LuArrowRight, LuCheckCircle2, LuAlertCircle } = LuIcons;
import SafeIcon from '../common/SafeIcon';

export default function Hero({ onAnalyze, isAnalyzing, error }) {
  const [url, setUrl] = useState('');

  const handleAnalyzeClick = () => {
    onAnalyze(url);
  };

  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      {/* Background Blobs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] max-w-7xl mx-auto pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-40 left-10 w-[30rem] h-[30rem] bg-indigo-400/20 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              Cheapo AI 2.0 is live
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-gray-900 mb-6 leading-[1.1]">
              Shop Smarter.<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Spend Less.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
              Paste any product link and let AI instantly compare prices, discover hidden discounts, and analyze whether it's the right time to buy.
            </p>

            <div className="max-w-xl">
              <div className={`bg-white p-2 rounded-2xl shadow-xl border transition-all ${error ? 'border-red-300 ring-4 ring-red-50' : 'border-gray-200'} mb-4 flex flex-col sm:flex-row gap-2`}>
                <div className="relative flex-grow flex items-center">
                  <SafeIcon icon={LuSearch} className="absolute left-4 text-gray-400 text-xl" />
                  <input 
                    type="url" 
                    value={url} 
                    onChange={(e) => setUrl(e.target.value)} 
                    placeholder="Paste an Amazon product link..." 
                    className="w-full pl-12 pr-4 py-3 bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-400 outline-none disabled:opacity-50"
                    disabled={isAnalyzing}
                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeClick()}
                  />
                </div>
                <button 
                  onClick={handleAnalyzeClick} 
                  disabled={isAnalyzing || !url.trim()}
                  className={`btn-primary py-3 sm:py-2 whitespace-nowrap min-w-[170px] ${isAnalyzing ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isAnalyzing ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </span>
                  ) : (
                    <>Analyze Product <SafeIcon icon={LuArrowRight} className="ml-2" /></>
                  )}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm font-medium mb-4 px-2">
                  <SafeIcon icon={LuAlertCircle} />
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <SafeIcon icon={LuCheckCircle2} className="text-green-500" /> Free to use
              </span>
              <span className="flex items-center gap-1.5">
                <SafeIcon icon={LuCheckCircle2} className="text-green-500" /> Amazon Verified
              </span>
            </div>
          </motion.div>

          <div className="hidden lg:block relative">
            <div className="absolute inset-0 bg-blue-600/10 blur-[100px] rounded-full"></div>
            <img 
              src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80" 
              alt="AI Shopping" 
              className="relative rounded-3xl shadow-2xl border border-white/50 object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}